import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Plus,
  Eye,
  EyeOff,
  Pencil,
  Trash2,
  Search,
  Users,
  RefreshCw,
  X,
  Mail,
  Phone,
  Briefcase,
  Building2,
  Clock3,
  IndianRupee,
  CalendarDays,
  MapPin,
  User,
  Hash,
  FileText,
  LockKeyhole,
  ShieldCheck,
  UserRoundCheck,
} from "lucide-react";

import "./admin.css";

function EmployeeList() {
  const navigate = useNavigate();

  // =====================================================
  // API
  // =====================================================

  const EMPLOYEE_API =
    "/api/employees";

  const DEPARTMENT_API =
    "/api/departments";

  const USERS_API =
    "/api/auth/users";

  // =====================================================
  // STATES
  // =====================================================

  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);

  const [search, setSearch] = useState("");

  const [loading, setLoading] = useState(true);
  const [departmentLoading, setDepartmentLoading] =
    useState(true);
  const [usersLoading, setUsersLoading] =
    useState(true);

  // Profile
  const [selectedEmployee, setSelectedEmployee] =
    useState(null);

  // Password visibility
  const [showPassword, setShowPassword] =
    useState(false);

  // Edit drawer
  const [editEmployee, setEditEmployee] =
    useState(null);

  const [updating, setUpdating] = useState(false);

  const [editForm, setEditForm] = useState({
    name: "",
    empCode: "",
    email: "",
    phone: "",
    designation: "",
    departmentId: "",
    experience: "",
    salary: "",
    joiningDate: "",
    location: "",
    remark: "",
  });

  // =====================================================
  // INITIAL LOAD
  // =====================================================

  useEffect(() => {
    fetchEmployees();
    fetchDepartments();
    fetchUsers();
  }, []);

  // =====================================================
  // FETCH EMPLOYEES
  // =====================================================

  const fetchEmployees = async () => {
    try {
      setLoading(true);

      const response = await fetch(EMPLOYEE_API);

      if (!response.ok) {
        throw new Error("Failed to fetch employees");
      }

      const data = await response.json();

      console.log(
        "EMPLOYEE API RESPONSE:",
        data
      );

      let employeeList = [];

      if (Array.isArray(data)) {
        employeeList = data;
      } else if (Array.isArray(data.data)) {
        employeeList = data.data;
      } else if (Array.isArray(data.content)) {
        employeeList = data.content;
      }

      setEmployees(employeeList);
    } catch (error) {
      console.error(
        "EMPLOYEE FETCH ERROR:",
        error
      );

      setEmployees([]);

      alert("Unable to load employees.");
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // FETCH DEPARTMENTS
  // =====================================================

  const fetchDepartments = async () => {
    try {
      setDepartmentLoading(true);

      const response = await fetch(
        DEPARTMENT_API
      );

      if (!response.ok) {
        throw new Error(
          "Failed to fetch departments"
        );
      }

      const data = await response.json();

      console.log(
        "DEPARTMENT API RESPONSE:",
        data
      );

      let departmentList = [];

      if (Array.isArray(data)) {
        departmentList = data;
      } else if (Array.isArray(data.data)) {
        departmentList = data.data;
      } else if (Array.isArray(data.content)) {
        departmentList = data.content;
      }

      setDepartments(departmentList);
    } catch (error) {
      console.error(
        "DEPARTMENT FETCH ERROR:",
        error
      );

      setDepartments([]);
    } finally {
      setDepartmentLoading(false);
    }
  };

  // =====================================================
  // FETCH USERS
  // =====================================================

  const fetchUsers = async () => {
    try {
      setUsersLoading(true);

      const response = await fetch(
        USERS_API
      );

      if (!response.ok) {
        throw new Error(
          "Failed to fetch users"
        );
      }

      const data = await response.json();

      console.log(
        "USERS API RESPONSE:",
        data
      );

      let userList = [];

      if (Array.isArray(data)) {
        userList = data;
      } else if (Array.isArray(data.data)) {
        userList = data.data;
      } else if (Array.isArray(data.content)) {
        userList = data.content;
      } else if (Array.isArray(data.users)) {
        userList = data.users;
      }

      setUsers(userList);
    } catch (error) {
      console.error(
        "USERS FETCH ERROR:",
        error
      );

      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  // =====================================================
  // GET DEPARTMENT NAME
  // =====================================================

  const getDepartmentName = (
    departmentId
  ) => {
    if (
      departmentId === null ||
      departmentId === undefined ||
      departmentId === ""
    ) {
      return "-";
    }

    const department = departments.find(
      (dept) =>
        Number(dept.id) ===
        Number(departmentId)
    );

    if (!department) {
      return `Department ${departmentId}`;
    }

    return (
      department.name ||
      department.departmentName ||
      department.title ||
      "-"
    );
  };

  // =====================================================
  // GET USER BY EMPLOYEE EMAIL
  // =====================================================

  const getEmployeeUser = (employee) => {
    if (!employee?.email) {
      return null;
    }

    const employeeEmail =
      employee.email
        .toString()
        .trim()
        .toLowerCase();

    const matchedUser = users.find(
      (user) =>
        user?.email
          ?.toString()
          .trim()
          .toLowerCase() === employeeEmail
    );

    return matchedUser || null;
  };

  // =====================================================
  // GET USER PASSWORD
  // =====================================================

  const getUserPassword = (employee) => {
    const user = getEmployeeUser(
      employee
    );

    if (!user) {
      return "";
    }

    return (
      user.temporaryPassword ||
      user.loginPassword ||
      user.rawPassword ||
      user.password ||
      ""
    );
  };

  // =====================================================
  // CHECK PASSWORD TYPE
  // =====================================================

  const isEncryptedPassword = (password) => {
    if (!password) {
      return false;
    }

    return (
      password.startsWith("$2a$") ||
      password.startsWith("$2b$") ||
      password.startsWith("$2y$")
    );
  };

  // =====================================================
  // OPEN PROFILE
  // =====================================================

  const openEmployeeProfile = (
    employee
  ) => {
    setSelectedEmployee(employee);
    setShowPassword(false);

    // Make sure latest user list is available
    if (!users.length) {
      fetchUsers();
    }
  };

  // =====================================================
  // CLOSE PROFILE
  // =====================================================

  const closeEmployeeProfile = () => {
    setSelectedEmployee(null);
    setShowPassword(false);
  };

  // =====================================================
  // OPEN EDIT DRAWER
  // =====================================================

  const openEditDrawer = (
    employee
  ) => {
    setEditEmployee(employee);

    setEditForm({
      name: employee.name || "",

      empCode:
        employee.empCode || "",

      email:
        employee.email || "",

      phone:
        employee.phone || "",

      designation:
        employee.designation || "",

      departmentId:
        employee.departmentId !==
          null &&
        employee.departmentId !==
          undefined
          ? employee.departmentId
          : "",

      experience:
        employee.experience || "",

      salary:
        employee.salary !== null &&
        employee.salary !== undefined
          ? employee.salary
          : "",

      joiningDate:
        employee.joiningDate || "",

      location:
        employee.location || "",

      remark:
        employee.remark || "",
    });

    setSelectedEmployee(null);
  };

  // =====================================================
  // EDIT CHANGE
  // =====================================================

  const handleEditChange = (e) => {
    const {
      name,
      value,
    } = e.target;

    setEditForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // =====================================================
  // UPDATE EMPLOYEE
  // =====================================================

  const updateEmployee = async (e) => {
    e.preventDefault();

    if (!editEmployee?.id) {
      alert("Employee ID not found.");
      return;
    }

    try {
      setUpdating(true);

      const payload = {
        name: editForm.name,
        empCode: editForm.empCode,
        email: editForm.email,
        phone: editForm.phone,
        designation: editForm.designation,

        departmentId:
          editForm.departmentId
            ? Number(
                editForm.departmentId
              )
            : null,

        experience:
          editForm.experience,

        salary:
          editForm.salary !== ""
            ? Number(
                editForm.salary
              )
            : null,

        joiningDate:
          editForm.joiningDate ||
          null,

        location:
          editForm.location,

        remark:
          editForm.remark || null,
      };

      console.log(
        "UPDATE EMPLOYEE PAYLOAD:",
        payload
      );

      const response =
        await fetch(
          `${EMPLOYEE_API}/${editEmployee.id}`,
          {
            method: "PUT",

            headers: {
              "Content-Type":
                "application/json",
            },

            body:
              JSON.stringify(
                payload
              ),
          }
        );

      if (!response.ok) {
        let message =
          "Failed to update employee.";

        try {
          const errorData =
            await response.json();

          message =
            errorData.message ||
            errorData.error ||
            message;
        } catch {
          // ignore
        }

        throw new Error(message);
      }

      const updatedData =
        await response.json();

      console.log(
        "UPDATED EMPLOYEE:",
        updatedData
      );

      setEmployees((prev) =>
        prev.map((emp) =>
          emp.id ===
          editEmployee.id
            ? updatedData
            : emp
        )
      );

      setEditEmployee(null);

      await fetchEmployees();
      await fetchUsers();

      alert(
        "Employee updated successfully!"
      );
    } catch (error) {
      console.error(
        "UPDATE EMPLOYEE ERROR:",
        error
      );

      alert(
        error.message ||
          "Failed to update employee."
      );
    } finally {
      setUpdating(false);
    }
  };

  // =====================================================
  // DELETE EMPLOYEE
  // =====================================================

  const deleteEmployee = async (id) => {
    const confirmDelete =
      window.confirm(
        "Are you sure you want to delete this employee?"
      );

    if (!confirmDelete) {
      return;
    }

    try {
      const response =
        await fetch(
          `${EMPLOYEE_API}/${id}`,
          {
            method: "DELETE",
          }
        );

      if (!response.ok) {
        let message =
          "Failed to delete employee.";

        try {
          const errorData =
            await response.json();

          message =
            errorData.message ||
            errorData.error ||
            message;
        } catch {
          // ignore
        }

        throw new Error(message);
      }

      setEmployees((prev) =>
        prev.filter(
          (emp) =>
            emp.id !== id
        )
      );

      if (
        selectedEmployee?.id === id
      ) {
        setSelectedEmployee(null);
      }

      if (
        editEmployee?.id === id
      ) {
        setEditEmployee(null);
      }

      alert(
        "Employee deleted successfully!"
      );
    } catch (error) {
      console.error(
        "DELETE ERROR:",
        error
      );

      alert(
        error.message ||
          "Failed to delete employee."
      );
    }
  };

  // =====================================================
  // SEARCH
  // =====================================================

  const filteredEmployees =
    employees.filter((emp) => {
      const text =
        search
          .toLowerCase()
          .trim();

      if (!text) {
        return true;
      }

      const departmentName =
        getDepartmentName(
          emp.departmentId
        );

      return (
        emp.name
          ?.toLowerCase()
          .includes(text) ||

        emp.email
          ?.toLowerCase()
          .includes(text) ||

        emp.phone
          ?.toLowerCase()
          .includes(text) ||

        emp.empCode
          ?.toLowerCase()
          .includes(text) ||

        emp.designation
          ?.toLowerCase()
          .includes(text) ||

        departmentName
          ?.toLowerCase()
          .includes(text) ||

        emp.location
          ?.toLowerCase()
          .includes(text) ||

        emp.remark
          ?.toLowerCase()
          .includes(text)
      );
    });

  // =====================================================
  // FORMAT SALARY
  // =====================================================

  const formatSalary = (salary) => {
    if (
      salary === null ||
      salary === undefined ||
      salary === ""
    ) {
      return "-";
    }

    return `₹ ${Number(
      salary
    ).toLocaleString("en-IN")}`;
  };

  // =====================================================
  // REFRESH
  // =====================================================

  const handleRefresh = () => {
    fetchEmployees();
    fetchDepartments();
    fetchUsers();
  };

  // =====================================================
  // ESCAPE
  // =====================================================

  useEffect(() => {
    const handleEscape = (
      event
    ) => {
      if (
        event.key === "Escape"
      ) {
        setSelectedEmployee(
          null
        );

        setEditEmployee(null);

        setShowPassword(false);
      }
    };

    document.addEventListener(
      "keydown",
      handleEscape
    );

    return () => {
      document.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, []);

  // =====================================================
  // UI
  // =====================================================

  return (
    <div className="employee-page">

      {/* =================================================
          HEADER
      ================================================= */}

      <div className="employee-header">

        <div className="employee-header-left">

          <div className="employee-title-icon">
            <Users size={22} />
          </div>

          <div>
            <h2>
              Employee Management
            </h2>

            <p>
              Manage and monitor all
              registered employees
            </p>
          </div>

        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >

          <button
            type="button"
            className="employee-action-btn view-btn"
            title="Refresh"
            onClick={
              handleRefresh
            }
          >
            <RefreshCw
              size={17}
            />
          </button>

          <button
            type="button"
            className="add-employee-btn"
            onClick={() =>
              navigate(
                "/employee-register"
              )
            }
          >
            <Plus size={18} />
            Add Employee
          </button>

        </div>

      </div>

      {/* =================================================
          TOOLBAR
      ================================================= */}

      <div className="employee-toolbar">

        <div className="employee-count">

          <div className="count-icon">
            <Users size={17} />
          </div>

          <div>
            <span>
              Total Employees
            </span>

            <strong>
              {employees.length}
            </strong>
          </div>

        </div>

        <div className="employee-search">

          <Search size={17} />

          <input
            type="text"
            placeholder="Search employee..."
            value={search}
            onChange={(e) =>
              setSearch(
                e.target.value
              )
            }
          />

        </div>

      </div>

      {/* =================================================
          TABLE
      ================================================= */}

      <div className="employee-table-container">

        <table className="employee-table">

          <thead>

            <tr>

              <th>#</th>

              <th>
                Employee
              </th>

              <th>
                Contact
              </th>

              <th>
                Designation
              </th>

              <th>
                Department
              </th>

              <th>
                Experience
              </th>

              <th>
                Location
              </th>

              <th className="employee-remark-header">
                Remark
              </th>

              <th>
                Actions
              </th>

            </tr>

          </thead>

          <tbody>

            {loading ||
            departmentLoading ? (

              <tr>

                <td
                  colSpan="9"
                  className="no-employee"
                >

                  <RefreshCw
                    size={30}
                    className="employee-spin"
                  />

                  <strong>
                    Loading Employees...
                  </strong>

                  <span>
                    Please wait...
                  </span>

                </td>

              </tr>

            ) : filteredEmployees.length >
              0 ? (

              filteredEmployees.map(
                (
                  emp,
                  index
                ) => {

                  const departmentName =
                    getDepartmentName(
                      emp.departmentId
                    );

                  const matchedUser =
                    getEmployeeUser(
                      emp
                    );

                  return (

                    <tr
                      key={
                        emp.id ||
                        index
                      }
                    >

                      {/* SERIAL */}

                      <td className="employee-serial">
                        {index + 1}
                      </td>

                      {/* EMPLOYEE */}

                      <td>

                        <div className="employee-info">

                          <div className="employee-avatar">
                            {emp.name
                              ?.charAt(
                                0
                              )
                              ?.toUpperCase() ||
                              "E"}
                          </div>

                          <div>

                            <strong>
                              {emp.name ||
                                "-"}
                            </strong>

                            <small>
                              Employee ID:{" "}
                              {emp.id ||
                                "-"}
                            </small>

                            <small>
                              Code:{" "}
                              {emp.empCode ||
                                "-"}
                            </small>

                          </div>

                        </div>

                      </td>

                      {/* CONTACT */}

                      <td>

                        <div className="employee-contact">

                          <span>
                            {emp.email ||
                              "-"}
                          </span>

                          <small>
                            {emp.phone ||
                              "-"}
                          </small>

                        </div>

                      </td>

                      {/* DESIGNATION */}

                      <td>

                        <span className="designation-badge">
                          {emp.designation ||
                            "-"}
                        </span>

                      </td>

                      {/* DEPARTMENT */}

                      <td>

                        <span className="department-badge">
                          {departmentName}
                        </span>

                      </td>

                      {/* EXPERIENCE */}

                      <td>
                        {emp.experience ||
                          "-"}
                      </td>

                      {/* LOCATION */}

                      <td>
                        {emp.location ||
                          "-"}
                      </td>

                      {/* REMARK */}

                      <td className="employee-remark-cell">

                        <span
                          className="employee-remark-text"
                          title={
                            emp.remark ||
                            "No remark"
                          }
                        >
                          {emp.remark ||
                            "-"}
                        </span>

                      </td>

                      {/* ACTIONS */}

                      <td>

                        <div className="employee-actions">

                          <button
                            type="button"
                            className="employee-action-btn view-btn"
                            title="View Profile"
                            onClick={() =>
                              openEmployeeProfile(
                                emp
                              )
                            }
                          >
                            <Eye
                              size={16}
                            />
                          </button>

                          <button
                            type="button"
                            className="employee-action-btn edit-btn"
                            title="Edit Employee"
                            onClick={() =>
                              openEditDrawer(
                                emp
                              )
                            }
                          >
                            <Pencil
                              size={16}
                            />
                          </button>

                          <button
                            type="button"
                            className="employee-action-btn delete-btn"
                            title="Delete Employee"
                            onClick={() =>
                              deleteEmployee(
                                emp.id
                              )
                            }
                          >
                            <Trash2
                              size={16}
                            />
                          </button>

                        </div>

                      </td>

                    </tr>

                  );
                }
              )

            ) : (

              <tr>

                <td
                  colSpan="9"
                  className="no-employee"
                >

                  <Users size={35} />

                  <strong>
                    No Employee Found
                  </strong>

                  <span>
                    {search
                      ? "Try a different search."
                      : "Register your first employee."}
                  </span>

                </td>

              </tr>

            )}

          </tbody>

        </table>

      </div>

      {/* =================================================
          PROFILE MODAL
      ================================================= */}

      {selectedEmployee && (

        <div
          className="employee-profile-overlay"
          onClick={
            closeEmployeeProfile
          }
        >

          <div
            className="employee-profile-modal"
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            {/* PROFILE HEADER */}

            <div className="employee-profile-header">

              <div className="employee-profile-header-left">

                <div className="employee-profile-avatar">

                  {selectedEmployee.name
                    ?.charAt(0)
                    ?.toUpperCase() ||
                    "E"}

                </div>

                <div>

                  <h2>
                    {selectedEmployee.name ||
                      "Employee"}
                  </h2>

                  <p>
                    {selectedEmployee.designation ||
                      "Employee Profile"}
                  </p>

                </div>

              </div>

              <div className="employee-profile-header-actions">

                {/* EDIT */}

                <button
                  type="button"
                  className="employee-profile-edit-icon"
                  title="Edit Employee"
                  onClick={() =>
                    openEditDrawer(
                      selectedEmployee
                    )
                  }
                >
                  <Pencil
                    size={17}
                  />
                </button>

                {/* CLOSE */}

                <button
                  type="button"
                  className="employee-profile-close"
                  title="Close"
                  onClick={
                    closeEmployeeProfile
                  }
                >
                  <X size={19} />
                </button>

              </div>

            </div>

            {/* PROFILE BODY */}

            <div className="employee-profile-body">

              {/* EMPLOYEE INFORMATION */}

              <div className="employee-profile-section">

                <div className="employee-profile-section-title">

                  <User size={17} />

                  Employee Information

                </div>

                <div className="employee-profile-grid">

                  <div className="employee-profile-field">

                    <span>
                      Employee ID
                    </span>

                    <strong>
                      {selectedEmployee.id ||
                        "-"}
                    </strong>

                  </div>

                  <div className="employee-profile-field">

                    <span>
                      Employee Code
                    </span>

                    <strong>
                      {selectedEmployee.empCode ||
                        "-"}
                    </strong>

                  </div>

                  <div className="employee-profile-field">

                    <span>
                      Full Name
                    </span>

                    <strong>
                      {selectedEmployee.name ||
                        "-"}
                    </strong>

                  </div>

                  <div className="employee-profile-field">

                    <span>
                      Designation
                    </span>

                    <strong>
                      {selectedEmployee.designation ||
                        "-"}
                    </strong>

                  </div>

                  <div className="employee-profile-field">

                    <span>
                      Department
                    </span>

                    <strong>
                      {getDepartmentName(
                        selectedEmployee.departmentId
                      )}
                    </strong>

                  </div>

                </div>

              </div>

              {/* CONTACT */}

              <div className="employee-profile-section">

                <div className="employee-profile-section-title">

                  <Phone size={17} />

                  Contact Information

                </div>

                <div className="employee-profile-grid">

                  <div className="employee-profile-field">

                    <span>
                      Email
                    </span>

                    <strong>
                      {selectedEmployee.email ||
                        "-"}
                    </strong>

                  </div>

                  <div className="employee-profile-field">

                    <span>
                      Phone
                    </span>

                    <strong>
                      {selectedEmployee.phone ||
                        "-"}
                    </strong>

                  </div>

                  <div className="employee-profile-field">

                    <span>
                      Location
                    </span>

                    <strong>
                      {selectedEmployee.location ||
                        "-"}
                    </strong>

                  </div>

                </div>

              </div>

              {/* EMPLOYMENT */}

              <div className="employee-profile-section">

                <div className="employee-profile-section-title">

                  <Briefcase size={17} />

                  Employment Information

                </div>

                <div className="employee-profile-grid">

                  <div className="employee-profile-field">

                    <span>
                      Experience
                    </span>

                    <strong>
                      {selectedEmployee.experience ||
                        "-"}
                    </strong>

                  </div>

                  <div className="employee-profile-field">

                    <span>
                      Salary
                    </span>

                    <strong>
                      {formatSalary(
                        selectedEmployee.salary
                      )}
                    </strong>

                  </div>

                  <div className="employee-profile-field">

                    <span>
                      Joining Date
                    </span>

                    <strong>
                      {selectedEmployee.joiningDate ||
                        "-"}
                    </strong>

                  </div>

                </div>

              </div>

              {/* =================================================
                  LOGIN CREDENTIALS
              ================================================= */}

              <div className="employee-profile-section employee-credentials-section">

                <div className="employee-profile-section-title">

                  <ShieldCheck
                    size={17}
                  />

                  Login Credentials

                </div>

                {usersLoading ? (

                  <div className="employee-login-loading">

                    <RefreshCw
                      size={15}
                      className="employee-spin"
                    />

                    Loading login details...

                  </div>

                ) : (

                  (() => {

                    const user =
                      getEmployeeUser(
                        selectedEmployee
                      );

                    const password =
                      getUserPassword(
                        selectedEmployee
                      );

                    if (!user) {

                      return (

                        <div className="employee-no-user">

                          <UserRoundCheck
                            size={18}
                          />

                          <div>

                            <strong>
                              Login account not found
                            </strong>

                            <span>
                              No user account was found
                              for this employee email.
                            </span>

                          </div>

                        </div>

                      );

                    }

                    return (

                      <>

                        <div className="employee-profile-grid">

                          {/* USER ID */}

                          <div className="employee-profile-field">

                            <span>
                              User ID
                            </span>

                            <strong>
                              {user.id ||
                                "-"}
                            </strong>

                          </div>

                          {/* LOGIN EMAIL */}

                          <div className="employee-profile-field">

                            <span>
                              Login Email
                            </span>

                            <div className="employee-credential-value">

                              <Mail
                                size={14}
                              />

                              <strong>
                                {user.email ||
                                  selectedEmployee.email ||
                                  "-"}
                              </strong>

                            </div>

                          </div>

                          {/* ROLE */}

                          <div className="employee-profile-field">

                            <span>
                              Role
                            </span>

                            <strong>
                              {user.role ||
                                "EMPLOYEE"}
                            </strong>

                          </div>

                          {/* PASSWORD */}

                          <div className="employee-profile-field">

                            <span>
                              Login Password
                            </span>

                            <div className="employee-password-box">

                              <div className="employee-credential-value">

                                <LockKeyhole
                                  size={14}
                                />

                                <strong>

                                  {!password
                                    ? "Not available"
                                    : isEncryptedPassword(
                                        password
                                      )
                                    ? "Password encrypted"
                                    : showPassword
                                    ? password
                                    : "••••••••••"}

                                </strong>

                              </div>

                              {password &&
                                !isEncryptedPassword(
                                  password
                                ) && (

                                  <button
                                    type="button"
                                    className="employee-password-toggle"
                                    title={
                                      showPassword
                                        ? "Hide Password"
                                        : "Show Password"
                                    }
                                    onClick={() =>
                                      setShowPassword(
                                        (prev) =>
                                          !prev
                                      )
                                    }
                                  >

                                    {showPassword ? (

                                      <EyeOff
                                        size={15}
                                      />

                                    ) : (

                                      <Eye
                                        size={15}
                                      />

                                    )}

                                  </button>

                                )}

                            </div>

                          </div>

                        </div>

                        <div className="employee-login-info">

                          <LockKeyhole
                            size={13}
                          />

                          <span>
                            Employee login uses the
                            account registered with
                            this email address.
                          </span>

                        </div>

                      </>

                    );

                  })()

                )}

              </div>

              {/* REMARK */}

              <div className="employee-profile-section">

                <div className="employee-profile-section-title">

                  <FileText
                    size={17}
                  />

                  Remark

                </div>

                <div className="employee-profile-remark">

                  {selectedEmployee.remark ||
                    "No remark available."}

                </div>

              </div>

            </div>

          </div>

        </div>

      )}

      {/* =================================================
          RIGHT EDIT DRAWER
      ================================================= */}

      {editEmployee && (

        <div
          className="employee-edit-overlay"
          onClick={() =>
            setEditEmployee(null)
          }
        >

          <div
            className="employee-edit-drawer"
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            {/* HEADER */}

            <div className="employee-edit-header">

              <div>

                <span className="employee-edit-small-title">
                  EMPLOYEE
                </span>

                <h2>
                  Edit Employee
                </h2>

                <p>
                  Update employee information
                </p>

              </div>

              <button
                type="button"
                className="employee-edit-close"
                title="Close"
                onClick={() =>
                  setEditEmployee(null)
                }
              >
                <X size={19} />
              </button>

            </div>

            {/* FORM */}

            <form
              className="employee-edit-form"
              onSubmit={
                updateEmployee
              }
            >

              <div className="employee-edit-grid">

                {/* NAME */}

                <div className="employee-edit-field">

                  <label>
                    Employee Name
                  </label>

                  <div className="employee-edit-input">

                    <User size={16} />

                    <input
                      type="text"
                      name="name"
                      value={
                        editForm.name
                      }
                      onChange={
                        handleEditChange
                      }
                      required
                    />

                  </div>

                </div>

                {/* EMP CODE */}

                <div className="employee-edit-field">

                  <label>
                    Employee Code
                  </label>

                  <div className="employee-edit-input">

                    <Hash size={16} />

                    <input
                      type="text"
                      name="empCode"
                      value={
                        editForm.empCode
                      }
                      onChange={
                        handleEditChange
                      }
                    />

                  </div>

                </div>

                {/* EMAIL */}

                <div className="employee-edit-field">

                  <label>
                    Email
                  </label>

                  <div className="employee-edit-input">

                    <Mail size={16} />

                    <input
                      type="email"
                      name="email"
                      value={
                        editForm.email
                      }
                      onChange={
                        handleEditChange
                      }
                      required
                    />

                  </div>

                </div>

                {/* PHONE */}

                <div className="employee-edit-field">

                  <label>
                    Phone
                  </label>

                  <div className="employee-edit-input">

                    <Phone size={16} />

                    <input
                      type="text"
                      name="phone"
                      value={
                        editForm.phone
                      }
                      onChange={
                        handleEditChange
                      }
                      required
                    />

                  </div>

                </div>

                {/* DESIGNATION */}

                <div className="employee-edit-field">

                  <label>
                    Designation
                  </label>

                  <div className="employee-edit-input">

                    <Briefcase
                      size={16}
                    />

                    <input
                      type="text"
                      name="designation"
                      value={
                        editForm.designation
                      }
                      onChange={
                        handleEditChange
                      }
                      required
                    />

                  </div>

                </div>

                {/* DEPARTMENT */}

                <div className="employee-edit-field">

                  <label>
                    Department
                  </label>

                  <div className="employee-edit-input">

                    <Building2
                      size={16}
                    />

                    <select
                      name="departmentId"
                      value={
                        editForm.departmentId
                      }
                      onChange={
                        handleEditChange
                      }
                    >

                      <option value="">
                        Select Department
                      </option>

                      {departments.map(
                        (dept) => (

                          <option
                            key={
                              dept.id
                            }
                            value={
                              dept.id
                            }
                          >
                            {dept.name ||
                              dept.departmentName ||
                              dept.title ||
                              `Department ${dept.id}`}
                          </option>

                        )
                      )}

                    </select>

                  </div>

                </div>

                {/* EXPERIENCE */}

                <div className="employee-edit-field">

                  <label>
                    Experience
                  </label>

                  <div className="employee-edit-input">

                    <Clock3
                      size={16}
                    />

                    <input
                      type="text"
                      name="experience"
                      value={
                        editForm.experience
                      }
                      onChange={
                        handleEditChange
                      }
                      placeholder="e.g. 5 Years"
                    />

                  </div>

                </div>

                {/* SALARY */}

                <div className="employee-edit-field">

                  <label>
                    Salary
                  </label>

                  <div className="employee-edit-input">

                    <IndianRupee
                      size={16}
                    />

                    <input
                      type="number"
                      name="salary"
                      value={
                        editForm.salary
                      }
                      onChange={
                        handleEditChange
                      }
                      min="0"
                    />

                  </div>

                </div>

                {/* JOINING DATE */}

                <div className="employee-edit-field">

                  <label>
                    Joining Date
                  </label>

                  <div className="employee-edit-input">

                    <CalendarDays
                      size={16}
                    />

                    <input
                      type="date"
                      name="joiningDate"
                      value={
                        editForm.joiningDate
                      }
                      onChange={
                        handleEditChange
                      }
                    />

                  </div>

                </div>

                {/* LOCATION */}

                <div className="employee-edit-field">

                  <label>
                    Location
                  </label>

                  <div className="employee-edit-input">

                    <MapPin
                      size={16}
                    />

                    <input
                      type="text"
                      name="location"
                      value={
                        editForm.location
                      }
                      onChange={
                        handleEditChange
                      }
                      placeholder="e.g. Bhopal"
                    />

                  </div>

                </div>

              </div>

              {/* REMARK */}

              <div className="employee-edit-field remark-field">

                <label>
                  Remark
                </label>

                <div className="employee-edit-textarea">

                  <FileText
                    size={16}
                  />

                  <textarea
                    name="remark"
                    value={
                      editForm.remark
                    }
                    onChange={
                      handleEditChange
                    }
                    placeholder="Enter employee remark..."
                    rows="3"
                  />

                </div>

              </div>

              {/* FOOTER */}

              <div className="employee-edit-footer">

                <button
                  type="button"
                  className="employee-edit-cancel"
                  onClick={() =>
                    setEditEmployee(
                      null
                    )
                  }
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="employee-edit-save"
                  disabled={updating}
                >

                  {updating ? (

                    <>
                      <RefreshCw
                        size={15}
                        className="employee-spin"
                      />

                      Updating...
                    </>

                  ) : (

                    <>
                      <Pencil
                        size={15}
                      />

                      Update Employee
                    </>

                  )}

                </button>

              </div>

            </form>

          </div>

        </div>

      )}

    </div>
  );
}

export default EmployeeList;